import { PivotQuery } from './PivotQuery';
export var PivotGroup;
(function (PivotGroup) {
    function make(series) {
        const keys = new Map();
        const column = new Map();
        const x = new Map();
        const row = new Map();
        const y = new Map();
        const marks = new Map();
        const names = new Map();
        const values = new Map();
        const sorts = new Map();
        return {
            keys,
            column,
            x,
            row,
            y,
            marks,
            names,
            values,
            sorts,
            series: new Map([[PivotQuery.Series.name(series), series]]),
        };
    }
    PivotGroup.make = make;
    function key(group) {
        const keyParts = new Array();
        for (const source of Array.from(group.keys.keys())) {
            keyParts.push(source);
        }
        return keyParts.sort().join(':');
    }
    PivotGroup.key = key;
    function hasField(group, field) {
        const { column, x, row, y, marks, values } = group;
        const channels = [column, x, row, y, marks, values];
        for (const fields of channels) {
            const groupField = fields.get(field.alias);
            if (groupField != null &&
                groupField.formula.source === field.formula.source) {
                return true;
            }
        }
        return false;
    }
    PivotGroup.hasField = hasField;
    function matches(target, source) {
        if (target.keys.size !== source.keys.size) {
            return false;
        }
        for (const keyValue of Array.from(target.keys.keys())) {
            if (!source.keys.has(keyValue)) {
                return false;
            }
        }
        return true;
    }
    PivotGroup.matches = matches;
    function merge(target, source) {
        return {
            ...target,
            series: new Map([
                ...Array.from(target.series),
                ...Array.from(source.series),
            ]),
            column: new Map([
                ...Array.from(target.column),
                ...Array.from(source.column),
            ]),
            x: new Map([...Array.from(target.x), ...Array.from(source.x)]),
            row: new Map([...Array.from(target.row), ...Array.from(source.row)]),
            y: new Map([...Array.from(target.y), ...Array.from(source.y)]),
            marks: new Map([
                ...Array.from(target.marks),
                ...Array.from(source.marks),
            ]),
            names: new Map([
                ...Array.from(target.names),
                ...Array.from(source.names),
            ]),
            values: new Map([
                ...Array.from(target.values),
                ...Array.from(source.values),
            ]),
        };
    }
    PivotGroup.merge = merge;
    let Column;
    (function (Column) {
        function add(initial, fieldsToAdd) {
            for (const field of fieldsToAdd) {
                const keys = new Map(initial.keys);
                const fields = new Map(initial.column);
                switch (field.role) {
                    case "DIMENSION": {
                        initial = {
                            ...initial,
                            column: fields.set(field.alias, field),
                            keys: keys.set(field.formula.source, field),
                        };
                        break;
                    }
                    case "MEASURE": {
                        initial = { ...initial, column: fields.set(field.alias, field) };
                        break;
                    }
                }
            }
            return initial;
        }
        Column.add = add;
    })(Column = PivotGroup.Column || (PivotGroup.Column = {}));
    let X;
    (function (X) {
        function add(initial, fieldsToAdd) {
            for (const field of fieldsToAdd) {
                const keys = new Map(initial.keys);
                const fields = new Map(initial.x);
                switch (field.role) {
                    case "DIMENSION": {
                        initial = {
                            ...initial,
                            x: fields.set(field.alias, field),
                            keys: keys.set(field.formula.source, field),
                        };
                        break;
                    }
                    case "MEASURE": {
                        initial = { ...initial, x: fields.set(field.alias, field) };
                        break;
                    }
                }
            }
            return initial;
        }
        X.add = add;
    })(X = PivotGroup.X || (PivotGroup.X = {}));
    let Row;
    (function (Row) {
        function add(initial, fieldsToAdd) {
            for (const field of fieldsToAdd) {
                const keys = new Map(initial.keys);
                const fields = new Map(initial.row);
                switch (field.role) {
                    case "DIMENSION": {
                        initial = {
                            ...initial,
                            row: fields.set(field.alias, field),
                            keys: keys.set(field.formula.source, field),
                        };
                        break;
                    }
                    case "MEASURE": {
                        initial = { ...initial, row: fields.set(field.alias, field) };
                        break;
                    }
                }
            }
            return initial;
        }
        Row.add = add;
    })(Row = PivotGroup.Row || (PivotGroup.Row = {}));
    let Y;
    (function (Y) {
        function add(initial, fieldsToAdd) {
            for (const field of fieldsToAdd) {
                const keys = new Map(initial.keys);
                const fields = new Map(initial.y);
                switch (field.role) {
                    case "DIMENSION": {
                        initial = {
                            ...initial,
                            y: fields.set(field.alias, field),
                            keys: keys.set(field.formula.source, field),
                        };
                        break;
                    }
                    case "MEASURE": {
                        initial = { ...initial, y: fields.set(field.alias, field) };
                        break;
                    }
                }
            }
            return initial;
        }
        Y.add = add;
    })(Y = PivotGroup.Y || (PivotGroup.Y = {}));
    let Marks;
    (function (Marks) {
        function add(initial, fieldsToAdd) {
            for (const field of fieldsToAdd) {
                const keys = new Map(initial.keys);
                const fields = new Map(initial.marks);
                switch (field.role) {
                    case "DIMENSION": {
                        initial = {
                            ...initial,
                            marks: fields.set(field.alias, field),
                            keys: keys.set(field.formula.source, field),
                        };
                        break;
                    }
                    case "MEASURE": {
                        initial = {
                            ...initial,
                            marks: fields.set(field.alias, field),
                        };
                        break;
                    }
                }
            }
            return initial;
        }
        Marks.add = add;
    })(Marks = PivotGroup.Marks || (PivotGroup.Marks = {}));
    let Names;
    (function (Names) {
        function add(initial, fields) {
            for (const field of fields) {
                const names = new Map(initial.names);
                if (field.role === "MEASURE") {
                    initial = {
                        ...initial,
                        names: names.set(field.alias, field),
                    };
                }
            }
            return initial;
        }
        Names.add = add;
    })(Names = PivotGroup.Names || (PivotGroup.Names = {}));
    let Values;
    (function (Values) {
        function add(initial, fields) {
            for (const field of fields) {
                const keys = new Map(initial.keys);
                const values = new Map(initial.values);
                if (field.role === "MEASURE") {
                    initial = {
                        ...initial,
                        values: values.set(field.alias, field),
                        keys: keys.set(field.formula.source, field),
                    };
                }
            }
            return initial;
        }
        Values.add = add;
    })(Values = PivotGroup.Values || (PivotGroup.Values = {}));
    let Sorts;
    (function (Sorts) {
        function add(initial, sortsToAdd) {
            const sorts = new Map(initial.sorts);
            for (const sort of sortsToAdd) {
                if (!sorts.has(sort.field.alias)) {
                    sorts.set(sort.field.alias, sort);
                }
            }
            return {
                ...initial,
                sorts,
            };
        }
        Sorts.add = add;
    })(Sorts = PivotGroup.Sorts || (PivotGroup.Sorts = {}));
})(PivotGroup || (PivotGroup = {}));
